import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  TrendingUp,
  Hash,
  X,
  ChevronDown,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, DateRangePicker } from '../ui';
import { useAttributionTagStore } from '@/stores/attributionTagStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import usePermissionsStore from '@/stores/permissionsStore';
import { DiscoveredHashtag } from '@/types/attributionTag';

interface HashtagData {
  hashtagId: number;
  hashtag: string;
  postCount: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalEmv: string;
  totalEmvValue: number;
  previousEmvValue: number;
}

interface DateRange {
  start: string;
  end: string;
}

interface DiscoveredHashtagsListProps {
  onPromoteClick: (hashtag: DiscoveredHashtag) => void;
  onHashtagSelect?: (hashtagId: number, hashtag: string, period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom', customDateRange?: DateRange | null) => void;
}

const DiscoveredHashtagsList: React.FC<DiscoveredHashtagsListProps> = ({
  onPromoteClick,
  onHashtagSelect,
}) => {
  const {
    discoveredHashtagsAnalytics,
    isDiscoveredAnalyticsLoading,
    loadDiscoveredHashtagsAnalytics,
  } = useAttributionTagStore();

  const { selectedCustomer } = useGlobalCustomerStore();
  const { permissions } = usePermissionsStore();

  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<'posts' | 'likes' | 'emv'>('emv');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedHashtag, setSelectedHashtag] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | '60days' | '90days' | 'custom'>('custom');
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 364);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  });
  const hashtagsPerPage = 50;

  // Load analytics when customer or period/date range changes
  useEffect(() => {
    if (selectedCustomer?.id) {
      loadDiscoveredHashtagsAnalytics(selectedPeriod, customDateRange);
    }
  }, [selectedCustomer?.id, selectedPeriod, customDateRange, loadDiscoveredHashtagsAnalytics]);

  const hashtagsData = discoveredHashtagsAnalytics?.hashtags || [];

  // Filter and sort hashtags
  const filteredHashtags = useMemo(() => {
    let filtered = [...hashtagsData];

    // Apply search filter
    if (searchInput) {
      filtered = filtered.filter((hashtag) =>
        hashtag.hashtag.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'emv':
          comparison = a.totalEmvValue - b.totalEmvValue;
          break;
        case 'posts':
          comparison = a.postCount - b.postCount;
          break;
        case 'likes':
          comparison = a.totalLikes - b.totalLikes;
          break;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [hashtagsData, searchInput, sortBy, sortOrder]);

  // Paginate results
  const paginatedHashtags = useMemo(() => {
    const startIndex = (currentPage - 1) * hashtagsPerPage;
    return filteredHashtags.slice(startIndex, startIndex + hashtagsPerPage);
  }, [filteredHashtags, currentPage, hashtagsPerPage]);

  const totalPages = Math.ceil(filteredHashtags.length / hashtagsPerPage);

  const handleSort = useCallback((field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  }, [sortBy]);

  const handleClearSearch = () => {
    setSearchInput('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowClick = (hashtag: HashtagData) => {
    setSelectedHashtag(hashtag.hashtag);
    if (onHashtagSelect) {
      onHashtagSelect(hashtag.hashtagId, hashtag.hashtag, selectedPeriod, selectedPeriod === 'custom' ? customDateRange : null);
    }
  };

  const handleDateRangeChange = (range: DateRange | null, period: string) => {
    if (period === 'custom' && range) {
      setSelectedPeriod('custom');
      setCustomDateRange(range);
    } else {
      setSelectedPeriod(period as typeof selectedPeriod);
      setCustomDateRange(null);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Convert HashtagData to DiscoveredHashtag for the promote action
  const toDiscoveredHashtag = (tag: HashtagData): DiscoveredHashtag => ({
    hashtag_id: tag.hashtagId,
    hashtag_text: tag.hashtag,
    description: null,
    status: null,
    post_count: tag.postCount,
    total_emv: tag.totalEmvValue,
    total_engagement: tag.totalLikes + tag.totalComments + tag.totalShares,
    latest_post_date: null,
    earliest_post_date: null,
    is_tracked: false,
    account_count: 0,
    created_at: undefined,
    updated_at: undefined,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Discovered Hashtags</h2>
          <p className="text-xs text-gray-600">
            Hashtags discovered from posts that can be promoted to attribution tags
          </p>
        </div>
      </div>

      {/* Search and Period Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search hashtags..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <DateRangePicker
            value={customDateRange}
            onChange={handleDateRangeChange}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!selectedCustomer?.id}
            selectedPeriod={selectedPeriod}
          />
        </div>
      </div>

      {/* Hashtags Table */}
      <Card className="overflow-hidden relative">
        {isDiscoveredAnalyticsLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-xs text-gray-900">Loading hashtags...</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Hashtag
                </th>
                <th
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('posts')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Posts</span>
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${
                        sortBy === 'posts' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('likes')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Engagement</span>
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${
                        sortBy === 'likes' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-900"
                  onClick={() => handleSort('emv')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total EMV</span>
                    <ChevronDown
                      className={`h-3 w-3 transition-transform ${
                        sortBy === 'emv' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                      }`}
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedHashtags.map((hashtag, index) => (
                <motion.tr
                  key={hashtag.hashtagId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className={`cursor-pointer transition-colors duration-200 ${
                    selectedHashtag === hashtag.hashtag
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : hashtag.totalEmvValue >= 500
                        ? 'bg-green-50 hover:bg-green-100'
                        : hashtag.totalEmvValue >= 200
                          ? 'bg-yellow-50 hover:bg-yellow-100'
                          : 'bg-red-50 hover:bg-red-100'
                  }`}
                  onClick={() => handleRowClick(hashtag)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md">
                        <Hash className="h-3 w-3 text-white" />
                      </div>
                      <p className="text-xs font-medium text-gray-900">{hashtag.hashtag}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs font-medium text-gray-900">
                      {formatNumber(hashtag.postCount)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <div className="flex items-center">
                        <Heart className="h-3 w-3 mr-0.5" />
                        {formatNumber(hashtag.totalLikes)}
                      </div>
                      <div className="flex items-center">
                        <MessageCircle className="h-3 w-3 mr-0.5" />
                        {formatNumber(hashtag.totalComments)}
                      </div>
                      {hashtag.totalShares > 0 && (
                        <div className="flex items-center">
                          <Share2 className="h-3 w-3 mr-0.5" />
                          {formatNumber(hashtag.totalShares)}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Eye className="h-3 w-3 mr-0.5" />
                        {formatNumber(hashtag.totalViews)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-normal text-gray-900">{hashtag.totalEmv}</span>
                      {hashtag.totalEmvValue !== hashtag.previousEmvValue &&
                        (hashtag.totalEmvValue > hashtag.previousEmvValue ? (
                          <div className="relative group">
                            <ArrowUp className="h-3 w-3 text-green-600" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              Increased from previous period
                            </div>
                          </div>
                        ) : (
                          <div className="relative group">
                            <ArrowDown className="h-3 w-3 text-red-600" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                              Decreased from previous period
                            </div>
                          </div>
                        ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(hashtag);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors duration-200"
                        title="View insights"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPromoteClick(toDiscoveredHashtag(hashtag));
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                        title="Promote to Attribution Tag"
                      >
                        <TrendingUp className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Row Count and Pagination */}
        {paginatedHashtags.length > 0 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-700">
                Showing{' '}
                <span className="font-medium">{filteredHashtags.length}</span> of{' '}
                <span className="font-medium">{hashtagsData.length}</span> tags
              </p>
              {totalPages > 1 && (
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1.5 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-3 py-1.5 border text-xs font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1.5 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isDiscoveredAnalyticsLoading && paginatedHashtags.length === 0 && (
          <div className="text-center py-12">
            <Hash className="mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hashtags found</h3>
            <p className="mt-1 text-xs text-gray-500">
              {searchInput
                ? 'Try adjusting your search terms.'
                : 'No hashtags match your current filters.'}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DiscoveredHashtagsList;
